import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { SUBSCRIPTIONS_PAGE_SIZE } from "../subscriptionsManageConstants";

/**
 * Normalizes the API response into a consistent shape.
 * Falls back to safe defaults when fields are missing or shape is unexpected.
 * @param {*} data - Decrypted API payload
 * @returns {{ items: Array, total: number, page: number, limit: number }}
 */
function normalizeListResponse(data) {
    if (!data || typeof data !== "object") {
        return { items: [], total: 0, page: 1, limit: SUBSCRIPTIONS_PAGE_SIZE };
    }
    const items = Array.isArray(data.items) ? data.items : [];
    const total = Number.isFinite(data.total) ? data.total : items.length;
    const page = Number.isFinite(data.page) ? data.page : 1;
    const limit = Number.isFinite(data.limit) ? data.limit : SUBSCRIPTIONS_PAGE_SIZE;
    return { items, total, page, limit };
}

/**
 * Builds the query string for the list endpoint from filter values.
 * Skips empty filter values so the API receives only meaningful params.
 * @param {Object} filters - { status, owner_type, subscription_type, payment_provider, page }
 * @returns {string} Query string starting with "?" or empty string
 */
function buildQuery(filters) {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.owner_type) params.set("owner_type", filters.owner_type);
    if (filters?.subscription_type) params.set("subscription_type", filters.subscription_type);
    if (filters?.payment_provider) params.set("payment_provider", filters.payment_provider);
    if (filters?.page) params.set("page", String(filters.page));
    params.set("limit", String(SUBSCRIPTIONS_PAGE_SIZE));
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

/**
 * Performs the GET /admin/subscriptions/list request and pushes results through setters.
 * Bails out silently if the request was aborted.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchList({ filters, signal, setData, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get(`/admin/subscriptions/list${buildQuery(filters)}`);
        if (!signal.aborted) setData(normalizeListResponse(data));
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible de charger les abonnements");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook returning the global subscriptions list state for the given filters.
 * Re-fetches on filters change and aborts in-flight requests on cleanup.
 * @param {Object} filters - { status, owner_type, subscription_type, page }
 * @returns {{ items: Array, total: number, page: number, limit: number, loading: boolean, error: string|null, refresh: Function }}
 */
function useSubscriptionsList(filters) {
    const [data, setData] = useState({ items: [], total: 0, page: 1, limit: SUBSCRIPTIONS_PAGE_SIZE });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    const key = `${filters?.status || ""}|${filters?.owner_type || ""}|${filters?.subscription_type || ""}|${filters?.payment_provider || ""}|${filters?.page || 1}|${tick}`;

    useEffect(() => {
        const controller = new AbortController();
        fetchList({ filters, signal: controller.signal, setData, setError, setLoading });
        return () => controller.abort();
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    const refresh = useCallback(() => setTick((t) => t + 1), []);
    return { ...data, loading, error, refresh };
}

export default useSubscriptionsList;
